// API client с httpOnly cookie auth.
//
// Токен живёт в httpOnly-cookie `edpad_jwt` (см. backend AuthRoutes.kt) —
// JS его не видит, что устраняет XSS-вектор кражи сессии. Браузер
// автоматически отправляет cookie на same-origin запросы при
// `credentials: 'include'`.
//
// Сессия (6.2): access-токен живёт 1 час, refresh — в httpOnly-cookie
// `edpad_refresh` (Path=/api/auth). На 401 клиент один раз вызывает
// POST /auth/refresh и повторяет исходный запрос. Параллельные запросы делят
// один refresh (single-flight): иначе второй refresh предъявил бы уже
// использованный токен, и сервер отозвал бы всю сессию как украденную.
//
// `currentTeacherIdStorage` — in-memory кэш ID текущего пользователя для
// модулей которые не имеют доступа к React-контексту (sync.ts включает
// teacherId в каждый push payload). AuthContext устанавливает значение
// после login/me и сбрасывает на logout.

// `?.` — чтобы модуль импортировался и вне Vite (node --test), где import.meta.env нет
const API_BASE = import.meta.env?.VITE_API_BASE_URL || '/api'

let currentTeacherId: string | null = null

export const currentTeacherIdStorage = {
  get: (): string | null => currentTeacherId,
  set: (id: string | null) => { currentTeacherId = id },
}

export class ApiError extends Error {
  // Явные поля вместо parameter properties: node --test (strip-only TS)
  // не поддерживает `constructor(public status ...)`.
  status: number
  body: unknown

  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.status = status
    this.body = body
  }
}

// ── Тихое обновление сессии ─────────────────────────────────────────────────

/**
 * Результат refresh:
 *  - `refreshed`  — новые cookie выставлены;
 *  - `superseded` — токен только что обновила другая вкладка (409): cookie уже
 *    свежие, достаточно повторить запрос;
 *  - `failed`     — сессии нет/истекла/отозвана → нужен вход.
 */
export type RefreshOutcome = 'refreshed' | 'superseded' | 'failed'

/** Эндпойнты, на 401 от которых refresh не делаем (это и есть вход/выход). */
const NO_REFRESH_PATHS = [
  '/auth/login', '/auth/register', '/auth/refresh', '/auth/logout', '/auth/reset',
]

function canRefresh(path: string): boolean {
  return !NO_REFRESH_PATHS.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'))
}

let refreshInFlight: Promise<RefreshOutcome> | null = null
const sessionExpiredListeners = new Set<() => void>()

/** Подписка на «сессия истекла, refresh не помог» (AuthContext → экран входа). */
export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener)
  return () => { sessionExpiredListeners.delete(listener) }
}

/** Single-flight POST /auth/refresh: одновременные вызовы получают один и тот же промис. */
export function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = (async (): Promise<RefreshOutcome> => {
      let outcome: RefreshOutcome
      try {
        const r = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
        outcome = r.ok ? 'refreshed' : r.status === 409 ? 'superseded' : 'failed'
      } catch {
        // Сеть недоступна — не разлогиниваем: исходная ошибка уйдёт вызывающему.
        return 'failed'
      }
      if (outcome === 'failed') sessionExpiredListeners.forEach((l) => l())
      return outcome
    })().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

/**
 * fetch к API с cookie-авторизацией и тихим обновлением: на 401 — одна попытка
 * refresh и один повтор. Повторный 401 возвращается как есть (без цикла).
 * `path` — относительно API_BASE.
 */
export async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = () => fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include', // отправляем httpOnly-cookie edpad_jwt
  })
  const resp = await doFetch()
  if (resp.status !== 401 || !canRefresh(path)) return resp
  const outcome = await refreshSession()
  if (outcome === 'failed') return resp
  return doFetch()
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json')
  }

  const resp = await authFetch(path, { ...init, headers })

  if (resp.status === 204) return undefined as T
  const text = await resp.text()
  const body = text ? safeParseJson(text) : null

  if (!resp.ok) {
    const msg =
      (body as { message?: string } | null)?.message ||
      `HTTP ${resp.status} ${resp.statusText}`
    throw new ApiError(resp.status, body, msg)
  }
  return body as T
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
